import React, { useState, useEffect } from 'react';
import esgQualityControlService from '../../../services/esg/esgQualityControlService';

const BreadQualityControl = () => {
  const [qualityData, setQualityData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [runningTest, setRunningTest] = useState(null);
  const [testResults, setTestResults] = useState(new Map());
  const [sandboxReceipt, setSandboxReceipt] = useState(null);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('kro_sandbox_receipt');
      if (raw) setSandboxReceipt(JSON.parse(raw));
    } catch (_) {}
  }, []);

  useEffect(() => {
    const loadQualityData = async () => {
      try {
        setLoading(true);
        const data = esgQualityControlService.generateQualityReport();
        setQualityData(data);
      } catch (error) {
        console.error('Error loading quality control data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadQualityData();

    // Subscribe to data updates
    const unsubscribe = esgQualityControlService.subscribe((data) => {
      setQualityData(prev => prev ? { ...prev, ...data } : data);
    });

    // Refresh data every 30 seconds
    const refreshInterval = setInterval(() => {
      esgQualityControlService.refreshData();
    }, 30000);

    return () => {
      unsubscribe();
      clearInterval(refreshInterval);
    };
  }, []);

  const runQualityTest = async (testType) => {
    setRunningTest(testType);
    try {
      const result = await esgQualityControlService.runAutomatedTest(testType);
      setTestResults(prev => new Map(prev.set(result.testId, result)));
    } catch (error) {
      console.error('Error running quality test:', error);
    } finally {
      setRunningTest(null);
    }
  };

  const getStatusColor = (score) => {
    if (score >= 95) return 'success';
    if (score >= 85) return 'info';
    if (score >= 75) return 'warning';
    return 'danger';
  };

  const getStatusBadge = (score) => {
    if (score >= 95) return { color: 'success', text: 'EXCELLENT' };
    if (score >= 85) return { color: 'info', text: 'GOOD' };
    if (score >= 75) return { color: 'warning', text: 'NEEDS IMPROVEMENT' };
    return { color: 'danger', text: 'CRITICAL' };
  };

  if (loading) {
    return (
      <div className="container-fluid">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading quality control data...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!qualityData) {
    return (
      <div className="container-fluid">
        <div className="alert alert-danger" role="alert">
          Error loading quality control data. Please try again later.
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid" style={{ color: 'black' }}>
      <div className="row mb-4">
        <div className="col-12">
          <h1 className="display-5 fw-bold mb-2" style={{ color: 'black' }}>
            ESG Quality Control Center
          </h1>
          <p className="lead" style={{ color: 'black' }}>
            Comprehensive quality assurance with industry-standard ESG compliance and continuous monitoring
          </p>
        </div>
      </div>

      {/* Quality Overview Cards */}
      <div className="row mb-4">
        <div className="col-md-3 mb-3">
          <div className="card border-0 shadow-sm">
            <div className="card-body text-center">
              <div className={`display-4 text-${getStatusColor(qualityData?.summary?.overallQualityScore || 0)} mb-2`}>
                {qualityData?.summary?.overallQualityScore?.toFixed(1) || '0.0'}
              </div>
              <h6 className="card-title" style={{ color: 'black' }}>Overall Quality Score</h6>
              <span className={`badge bg-${getStatusBadge(qualityData?.summary?.overallQualityScore || 0).color}`}>
                {getStatusBadge(qualityData?.summary?.overallQualityScore || 0).text}
              </span>
            </div>
          </div>
        </div>
        
        <div className="col-md-3 mb-3">
          <div className="card border-0 shadow-sm">
            <div className="card-body text-center">
              <div className="display-4 text-success mb-2">
                {qualityData?.summary?.complianceScore?.toFixed(1) || '0.0'}
              </div>
              <h6 className="card-title" style={{ color: 'black' }}>Compliance Score</h6>
              <small className="text-muted" style={{ color: 'black !important' }}>
                {qualityData?.summary?.passedTests || 0}/{qualityData?.summary?.activeTests || 0} Tests Passed
              </small>
            </div>
          </div>
        </div>
        
        <div className="col-md-3 mb-3">
          <div className="card border-0 shadow-sm">
            <div className="card-body text-center">
              <div className="display-4 text-info mb-2">
                {qualityData?.summary?.stakeholderSatisfaction?.toFixed(1) || '0.0'}
              </div>
              <h6 className="card-title" style={{ color: 'black' }}>Stakeholder Satisfaction</h6>
              <small className="text-muted" style={{ color: 'black !important' }}>
                Out of 5.0
              </small>
            </div>
          </div>
        </div>
        
        <div className="col-md-3 mb-3">
          <div className="card border-0 shadow-sm">
            <div className="card-body text-center">
              <div className="display-4 text-warning mb-2">
                {qualityData?.summary?.automationCoverage?.toFixed(1) || '0.0'}%
              </div>
              <h6 className="card-title" style={{ color: 'black' }}>Automation Coverage</h6>
              <small className="text-muted" style={{ color: 'black !important' }}>
                Automated Testing
              </small>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="row mb-4">
        <div className="col-12">
          <ul className="nav nav-tabs">
            <li className="nav-item">
              <button 
                className={`nav-link ${activeTab === 'overview' ? 'active' : ''}`}
                onClick={() => setActiveTab('overview')}
                style={{ color: activeTab === 'overview' ? 'white' : 'black' }}
              >
                Quality Overview
              </button>
            </li>
            <li className="nav-item">
              <button 
                className={`nav-link ${activeTab === 'testing' ? 'active' : ''}`}
                onClick={() => setActiveTab('testing')}
                style={{ color: activeTab === 'testing' ? 'white' : 'black' }}
              >
                Quality Assurance
              </button>
            </li>
            <li className="nav-item">
              <button 
                className={`nav-link ${activeTab === 'accessibility' ? 'active' : ''}`}
                onClick={() => setActiveTab('accessibility')}
                style={{ color: activeTab === 'accessibility' ? 'white' : 'black' }}
              >
                Accessibility Testing
              </button>
            </li>
            <li className="nav-item">
              <button 
                className={`nav-link ${activeTab === 'sustainability' ? 'active' : ''}`}
                onClick={() => setActiveTab('sustainability')}
                style={{ color: activeTab === 'sustainability' ? 'white' : 'black' }}
              >
                Sustainability
              </button>
            </li>
            <li className="nav-item">
              <button 
                className={`nav-link ${activeTab === 'ethics' ? 'active' : ''}`}
                onClick={() => setActiveTab('ethics')}
                style={{ color: activeTab === 'ethics' ? 'white' : 'black' }}
              >
                Ethics & Privacy
              </button>
            </li>
            <li className="nav-item">
              <button 
                className={`nav-link ${activeTab === 'feedback' ? 'active' : ''}`}
                onClick={() => setActiveTab('feedback')}
                style={{ color: activeTab === 'feedback' ? 'white' : 'black' }}
              >
                Stakeholder Feedback
              </button>
            </li>
            <li className="nav-item">
              <button 
                className={`nav-link ${activeTab === 'monitoring' ? 'active' : ''}`}
                onClick={() => setActiveTab('monitoring')}
                style={{ color: activeTab === 'monitoring' ? 'white' : 'black' }}
              >
                Live Monitoring
              </button>
            </li>
            <li className="nav-item">
              <button 
                className={`nav-link ${activeTab === 'benchmarking' ? 'active' : ''}`}
                onClick={() => setActiveTab('benchmarking')}
                style={{ color: activeTab === 'benchmarking' ? 'white' : 'black' }}
              >
                Benchmarking
              </button>
            </li>
            <li className="nav-item">
              <button 
                className={`nav-link ${activeTab === 'node-uptime' ? 'active' : ''}`}
                onClick={() => setActiveTab('node-uptime')}
                style={{ color: activeTab === 'node-uptime' ? 'white' : 'black' }}
              >
                <i className="bi bi-hdd-network me-1"></i>Node Uptime & Settlement Protocol
              </button>
            </li>
          </ul>
        </div>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {/* Quality Overview Tab */}
        {activeTab === 'overview' && (
          <div className="tab-pane fade show active">
            <div className="row">
              {/* Quality Trends Chart */}
              <div className="col-md-8 mb-4">
                <div className="card border-0 shadow-sm">
                  <div className="card-header bg-primary text-white">
                    <h5 className="mb-0">Quality Trends (12 Months)</h5>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      {qualityData?.trends?.overallQuality?.slice(-6).map((trend, index) => (
                        <div key={index} className="col-md-2 text-center mb-3">
                          <div className="fw-bold" style={{ color: 'black' }}>{trend.value}</div>
                          <small className="text-muted" style={{ color: 'black !important' }}>{trend.month}</small>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Quick Actions */}
              <div className="col-md-4 mb-4">
                <div className="card border-0 shadow-sm">
                  <div className="card-header bg-success text-white">
                    <h5 className="mb-0">Quick Actions</h5>
                  </div>
                  <div className="card-body">
                    <div className="d-grid gap-2">
                      {['accessibility', 'performance', 'security', 'sustainability'].map(testType => (
                        <button 
                          key={testType}
                          className="btn btn-outline-primary btn-sm"
                          onClick={() => runQualityTest(testType)}
                          disabled={runningTest === testType}
                          style={{ color: 'black', borderColor: 'black' }}
                        >
                          {runningTest === testType ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                              Running {testType} test...
                            </>
                          ) : (
                            `Run ${testType.charAt(0).toUpperCase() + testType.slice(1)} Test`
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Compliance Status */}
            <div className="row">
              <div className="col-12">
                <div className="card border-0 shadow-sm">
                  <div className="card-header bg-info text-white">
                    <h5 className="mb-0">Compliance Status</h5>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      {Object.entries(qualityData?.compliance || {}).map(([standard, data]) => (
                        <div key={standard} className="col-md-3 mb-3">
                          <div className="card">
                            <div className="card-body text-center">
                              <h6 className="card-title text-uppercase" style={{ color: 'black' }}>
                                {standard}
                              </h6>
                              <div className={`h4 text-${getStatusColor(data.score)}`}>
                                {data.score}
                              </div>
                              <span className={`badge bg-${data.status === 'Certified' || data.status === 'Compliant' ? 'success' : 'warning'}`}>
                                {data.status}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Quality Assurance Tab */}
        {activeTab === 'testing' && (
          <div className="tab-pane fade show active">
            <div className="row">
              {Object.entries(qualityData?.testing || {}).map(([testType, testData]) => (
                <div key={testType} className="col-md-6 mb-4">
                  <div className="card border-0 shadow-sm">
                    <div className={`card-header bg-${getStatusColor(testData.score)} text-white`}>
                      <h5 className="mb-0">{testData.testName}</h5>
                    </div>
                    <div className="card-body">
                      <div className="row mb-3">
                        <div className="col-6">
                          <strong style={{ color: 'black' }}>Status:</strong>
                          <span className={`badge bg-${testData.status === 'passed' ? 'success' : 'danger'} ms-2`}>
                            {testData.status.toUpperCase()}
                          </span>
                        </div>
                        <div className="col-6">
                          <strong style={{ color: 'black' }}>Score:</strong> 
                          <span style={{ color: 'black' }}> {testData.score}/100</span>
                        </div>
                      </div>
                      
                      {testData.testCases && (
                        <div className="mb-3">
                          <h6 style={{ color: 'black' }}>Test Cases:</h6>
                          <div className="row text-center">
                            <div className="col-3">
                              <div className="text-success fw-bold">{testData.testCases.passed}</div>
                              <small style={{ color: 'black' }}>Passed</small>
                            </div>
                            <div className="col-3">
                              <div className="text-danger fw-bold">{testData.testCases.failed}</div>
                              <small style={{ color: 'black' }}>Failed</small>
                            </div>
                            <div className="col-3">
                              <div className="text-warning fw-bold">{testData.testCases.skipped}</div>
                              <small style={{ color: 'black' }}>Skipped</small>
                            </div>
                            <div className="col-3">
                              <div className="text-info fw-bold">{testData.testCases.total}</div>
                              <small style={{ color: 'black' }}>Total</small>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {testData.esgAlignment && (
                        <div>
                          <h6 style={{ color: 'black' }}>ESG Alignment:</h6>
                          <div className="row">
                            {Object.entries(testData.esgAlignment).map(([category, count]) => (
                              <div key={category} className="col-6 mb-2">
                                <span style={{ color: 'black' }}>
                                  {category.charAt(0).toUpperCase() + category.slice(1)}: {count}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Accessibility Testing Tab */}
        {activeTab === 'accessibility' && (
          <div className="tab-pane fade show active">
            <div className="row mb-4">
              <div className="col-md-4">
                <div className="card border-0 shadow-sm">
                  <div className="card-header bg-success text-white">
                    <h5 className="mb-0">WCAG Compliance</h5>
                  </div>
                  <div className="card-body">
                    <div className="mb-2">
                      <div className="d-flex justify-content-between">
                        <span style={{ color: 'black' }}>Level A:</span>
                        <span style={{ color: 'black' }}>{qualityData?.accessibility?.wcagCompliance?.levelA}%</span>
                      </div>
                      <div className="progress mb-2">
                        <div className="progress-bar bg-success" style={{width: `${qualityData?.accessibility?.wcagCompliance?.levelA}%`}}></div>
                      </div>
                    </div>
                    <div className="mb-2">
                      <div className="d-flex justify-content-between">
                        <span style={{ color: 'black' }}>Level AA:</span>
                        <span style={{ color: 'black' }}>{qualityData?.accessibility?.wcagCompliance?.levelAA}%</span>
                      </div>
                      <div className="progress mb-2">
                        <div className="progress-bar bg-info" style={{width: `${qualityData?.accessibility?.wcagCompliance?.levelAA}%`}}></div>
                      </div>
                    </div>
                    <div className="mb-2">
                      <div className="d-flex justify-content-between">
                        <span style={{ color: 'black' }}>Level AAA:</span>
                        <span style={{ color: 'black' }}>{qualityData?.accessibility?.wcagCompliance?.levelAAA}%</span>
                      </div>
                      <div className="progress">
                        <div className="progress-bar bg-warning" style={{width: `${qualityData?.accessibility?.wcagCompliance?.levelAAA}%`}}></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="col-md-4">
                <div className="card border-0 shadow-sm">
                  <div className="card-header bg-primary text-white">
                    <h5 className="mb-0">Automated Tests</h5>
                  </div>
                  <div className="card-body">
                    {Object.entries(qualityData?.accessibility?.automatedAccessibilityTests || {}).map(([tool, data]) => (
                      <div key={tool} className="mb-3">
                        <h6 className="text-capitalize" style={{ color: 'black' }}>{tool}:</h6>
                        {typeof data === 'object' && data !== null ? (
                          <div>
                            {data.score && (
                              <div>
                                <span style={{ color: 'black' }}>Score: {data.score}</span>
                                {data.issues && <span style={{ color: 'black' }}> | Issues: {data.issues}</span>}
                              </div>
                            )}
                            {data.accessibilityScore && (
                              <div>
                                <span style={{ color: 'black' }}>Accessibility: {data.accessibilityScore}</span>
                                {data.performanceScore && <span style={{ color: 'black' }}> | Performance: {data.performanceScore}</span>}
                                {data.bestPracticesScore && <span style={{ color: 'black' }}> | Best Practices: {data.bestPracticesScore}</span>}
                                {data.seoScore && <span style={{ color: 'black' }}> | SEO: {data.seoScore}</span>}
                              </div>
                            )}
                            {data.errors !== undefined && (
                              <div>
                                <span style={{ color: 'black' }}>Errors: {data.errors}</span>
                                {data.alerts && <span style={{ color: 'black' }}> | Alerts: {data.alerts}</span>}
                                {data.features && <span style={{ color: 'black' }}> | Features: {data.features}</span>}
                              </div>
                            )}
                            {!data.score && !data.accessibilityScore && data.errors === undefined && (
                              <span style={{ color: 'black' }}>
                                {Object.entries(data).map(([key, value]) => `${key}: ${value}`).join(' | ')}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span style={{ color: 'black' }}>Score: {data}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              <div className="col-md-4">
                <div className="card border-0 shadow-sm">
                  <div className="card-header bg-info text-white">
                    <h5 className="mb-0">Assistive Technology</h5>
                  </div>
                  <div className="card-body">
                    {Object.entries(qualityData?.accessibility?.assistiveTechnologyTesting?.screenReaders || {}).map(([reader, data]) => (
                      <div key={reader} className="mb-2">
                        <div className="d-flex justify-content-between">
                          <span className="text-capitalize" style={{ color: 'black' }}>{reader}:</span>
                          <span style={{ color: 'black' }}>{data.compatibility}%</span>
                        </div>
                        <div className="progress mb-2">
                          <div className="progress-bar" style={{width: `${data.compatibility}%`}}></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Sustainability Tab */}
        {activeTab === 'sustainability' && (
          <div className="tab-pane fade show active">
            <div className="row">
              <div className="col-md-6 mb-4">
                <div className="card border-0 shadow-sm">
                  <div className="card-header bg-success text-white">
                    <h5 className="mb-0">Carbon Footprint Assessment</h5>
                  </div>
                  <div className="card-body">
                    <div className="mb-3">
                      <strong style={{ color: 'black' }}>Total Annual Emissions:</strong>
                      <span style={{ color: 'black' }}> {qualityData?.sustainability?.carbonFootprintAssessment?.totalEmissions} kg CO₂</span>
                    </div>
                    <div className="mb-3">
                      <strong style={{ color: 'black' }}>Per User Session:</strong>
                      <span style={{ color: 'black' }}> {qualityData?.sustainability?.carbonFootprintAssessment?.perUserSession} kg CO₂</span>
                    </div>
                    <div className="mb-3">
                      <strong style={{ color: 'black' }}>Reduction from Baseline:</strong>
                      <span className="text-success"> {qualityData?.sustainability?.carbonFootprintAssessment?.reductionFromBaseline}%</span>
                    </div>
                    <div className="mb-3">
                      <strong style={{ color: 'black' }}>Certification:</strong>
                      <span className="badge bg-success ms-2">
                        {qualityData?.sustainability?.carbonFootprintAssessment?.certificationStatus}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="col-md-6 mb-4">
                <div className="card border-0 shadow-sm">
                  <div className="card-header bg-primary text-white">
                    <h5 className="mb-0">Energy Efficiency</h5>
                  </div>
                  <div className="card-body">
                    {Object.entries(qualityData?.sustainability?.energyEfficiencyTesting || {}).map(([category, score]) => {
                      if (category === 'overallScore') return null;
                      return (
                        <div key={category} className="mb-2">
                          <div className="d-flex justify-content-between">
                            <span className="text-capitalize" style={{ color: 'black' }}>
                              {category.replace(/([A-Z])/g, ' $1').trim()}:
                            </span>
                            <span style={{ color: 'black' }}>{score}%</span>
                          </div>
                          <div className="progress mb-2">
                            <div className="progress-bar bg-primary" style={{width: `${score}%`}}></div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Ethics & Privacy Tab */}
        {activeTab === 'ethics' && (
          <div className="tab-pane fade show active">
            <div className="row">
              <div className="col-md-6 mb-4">
                <div className="card border-0 shadow-sm">
                  <div className="card-header bg-warning text-dark">
                    <h5 className="mb-0">AI Ethics Assessment</h5>
                  </div>
                  <div className="card-body">
                    <h6 style={{ color: 'black' }}>Bias Detection:</h6>
                    {Object.entries(qualityData?.ethics?.aiEthicsAssessment?.biasDetection || {}).map(([biasType, percentage]) => {
                      if (biasType === 'overallBiasScore') return null;
                      return (
                        <div key={biasType} className="mb-2">
                          <div className="d-flex justify-content-between">
                            <span className="text-capitalize" style={{ color: 'black' }}>
                              {biasType.replace(/([A-Z])/g, ' $1').trim()}:
                            </span>
                            <span style={{ color: 'black' }}>{percentage}%</span>
                          </div>
                        </div>
                      );
                    })}
                    <div className="mt-3">
                      <strong style={{ color: 'black' }}>Overall Bias Score:</strong>
                      <span className={`ms-2 text-${qualityData?.ethics?.aiEthicsAssessment?.biasDetection?.overallBiasScore < 5 ? 'success' : 'warning'}`}>
                        {qualityData?.ethics?.aiEthicsAssessment?.biasDetection?.overallBiasScore}% (lower is better)
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="col-md-6 mb-4">
                <div className="card border-0 shadow-sm">
                  <div className="card-header bg-info text-white">
                    <h5 className="mb-0">Privacy Compliance</h5>
                  </div>
                  <div className="card-body">
                    <h6 style={{ color: 'black' }}>GDPR Compliance:</h6>
                    {Object.entries(qualityData?.privacy?.gdprCompliance || {}).map(([principle, score]) => {
                      if (principle === 'overallScore') return null;
                      return (
                        <div key={principle} className="mb-2">
                          <div className="d-flex justify-content-between">
                            <span className="text-capitalize" style={{ color: 'black' }}>
                              {principle.replace(/([A-Z])/g, ' $1').trim()}:
                            </span>
                            <span style={{ color: 'black' }}>{score}%</span>
                          </div>
                        </div>
                      );
                    })}
                    <div className="mt-3">
                      <strong style={{ color: 'black' }}>Overall GDPR Score:</strong>
                      <span className="text-success ms-2">
                        {qualityData?.privacy?.gdprCompliance?.overallScore}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Stakeholder Feedback Tab */}
        {activeTab === 'feedback' && (
          <div className="tab-pane fade show active">
            <div className="row">
              {Object.entries(qualityData?.feedback?.feedbackCollection || {}).map(([stakeholder, data]) => (
                <div key={stakeholder} className="col-md-6 mb-4">
                  <div className="card border-0 shadow-sm">
                    <div className="card-header bg-secondary text-white">
                      <h5 className="mb-0 text-capitalize">{stakeholder.replace('Feedback', ' Feedback')}</h5>
                    </div>
                    <div className="card-body">
                      <div className="mb-2">
                        <strong style={{ color: 'black' }}>Volume:</strong>
                        <span style={{ color: 'black' }}> {data.volume} responses</span>
                      </div>
                      {data.satisfaction && (
                        <div className="mb-2">
                          <strong style={{ color: 'black' }}>Satisfaction:</strong>
                          <span style={{ color: 'black' }}> {data.satisfaction} / 5.0</span>
                        </div>
                      )}
                      {data.nps && (
                        <div className="mb-2">
                          <strong style={{ color: 'black' }}>NPS Score:</strong>
                          <span style={{ color: 'black' }}> {data.nps}</span>
                        </div>
                      )}
                      {data.sentimentScore && (
                        <div className="mb-2">
                          <strong style={{ color: 'black' }}>Sentiment:</strong>
                          <span className={`text-${data.sentimentScore > 70 ? 'success' : data.sentimentScore > 50 ? 'warning' : 'danger'}`}>
                            {data.sentimentScore}% positive
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Live Monitoring Tab */}
        {activeTab === 'monitoring' && (
          <div className="tab-pane fade show active">
            <div className="row">
              <div className="col-md-6 mb-4">
                <div className="card border-0 shadow-sm">
                  <div className="card-header bg-success text-white">
                    <h5 className="mb-0">Real-Time Monitoring</h5>
                  </div>
                  <div className="card-body">
                    <div className="mb-3">
                      <strong style={{ color: 'black' }}>System Uptime:</strong>
                      <span className="text-success ms-2">{qualityData?.monitoring?.realTimeMonitoring?.uptime}%</span>
                    </div>
                    <div className="mb-3">
                      <strong style={{ color: 'black' }}>Response Time:</strong>
                      <span style={{ color: 'black' }}> {qualityData?.monitoring?.realTimeMonitoring?.responseTime}s</span>
                    </div>
                    <div className="mb-3">
                      <strong style={{ color: 'black' }}>Error Rate:</strong>
                      <span className="text-success"> {qualityData?.monitoring?.realTimeMonitoring?.errorRate}%</span>
                    </div>
                    <div className="mb-3">
                      <strong style={{ color: 'black' }}>Throughput:</strong>
                      <span style={{ color: 'black' }}> {qualityData?.monitoring?.realTimeMonitoring?.throughput} req/min</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="col-md-6 mb-4">
                <div className="card border-0 shadow-sm">
                  <div className="card-header bg-warning text-dark">
                    <h5 className="mb-0">Active Alerts</h5>
                  </div>
                  <div className="card-body">
                    <div className="mb-2">
                      <span className="badge bg-danger me-2">Critical</span>
                      <span style={{ color: 'black' }}>{qualityData?.monitoring?.alertingSystem?.criticalAlerts}</span>
                    </div>
                    <div className="mb-2">
                      <span className="badge bg-warning me-2">Warning</span>
                      <span style={{ color: 'black' }}>{qualityData?.monitoring?.alertingSystem?.warningAlerts}</span>
                    </div>
                    <div className="mb-2">
                      <span className="badge bg-info me-2">Info</span>
                      <span style={{ color: 'black' }}>{qualityData?.monitoring?.alertingSystem?.infoAlerts}</span>
                    </div>
                    <div className="mt-3">
                      <small style={{ color: 'black' }}>
                        Avg Response: {qualityData?.monitoring?.alertingSystem?.alertResponse} min
                      </small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Benchmarking Tab */}
        {activeTab === 'benchmarking' && (
          <div className="tab-pane fade show active">
            <div className="row">
              {Object.entries(qualityData?.benchmarking?.industryComparison || {}).map(([metric, data]) => (
                <div key={metric} className="col-md-4 mb-4">
                  <div className="card border-0 shadow-sm">
                    <div className="card-header bg-primary text-white">
                      <h5 className="mb-0 text-capitalize">{metric.replace('Score', ' Score')}</h5>
                    </div>
                    <div className="card-body">
                      <div className="mb-2">
                        <strong style={{ color: 'black' }}>Our Score:</strong>
                        <span className="text-success ms-2">{data.ourScore}</span>
                      </div>
                      <div className="mb-2">
                        <strong style={{ color: 'black' }}>Industry Average:</strong>
                        <span style={{ color: 'black' }}> {data.industryAverage}</span>
                      </div>
                      <div className="mb-2">
                        <strong style={{ color: 'black' }}>Top Quartile:</strong>
                        <span style={{ color: 'black' }}> {data.topQuartile}</span>
                      </div>
                      <div className="mb-2">
                        <strong style={{ color: 'black' }}>Best in Class:</strong>
                        <span style={{ color: 'black' }}> {data.bestInClass}</span>
                      </div>
                      <div>
                        <strong style={{ color: 'black' }}>Percentile Rank:</strong>
                        <span className="text-info ms-2">{data.percentileRank}th</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Node Uptime & Settlement Protocol Tab */}
        {activeTab === 'node-uptime' && (
          <div className="tab-pane fade show active">
            {/* Sandbox Sync Banner */}
            {sandboxReceipt && (
              <div className="alert border-0 mb-4" style={{ background: 'linear-gradient(135deg, #0f172a, #1e293b)', color: '#e2e8f0' }}>
                <div className="d-flex align-items-center gap-2 mb-1">
                  <span className="rounded-circle" style={{ width: 10, height: 10, background: '#10b981', display: 'inline-block', boxShadow: '0 0 6px #10b981' }}></span>
                  <strong>Settlement Protocol Synced from Settlement Hub</strong>
                  <span className="badge bg-success ms-2">E2E PASSED</span>
                </div>
                <small style={{ color: '#94a3b8' }}>
                  Root Key: <code style={{ color: '#c9a84c' }}>{sandboxReceipt.rootRegistryKey}</code> · 
                  Canton Contract: <code style={{ color: '#38bdf8' }}>{sandboxReceipt.cantonContractId}</code>
                </small>
              </div>
            )}

            {/* Node Uptime Cards */}
            <div className="row mb-4">
              {[
                { node: 'KRO-Mainnet-1', operator: 'Infra Singularity', type: 'Canton Participant', uptime: 99.87, lastBlock: sandboxReceipt?.blockHeight || 18942310, status: 'ONLINE', icon: 'bi-lightning-charge-fill', color: '#38bdf8' },
                { node: 'pio-mainnet-1', operator: 'Provenance / Figure Markets', type: 'Provenance Validator', uptime: 99.94, lastBlock: (sandboxReceipt?.blockHeight || 18942310) + 1200, status: 'ONLINE', icon: 'bi-box-seam-fill', color: '#10b981' },
                { node: 'BitGo Enterprise', operator: 'BitGo, Inc.', type: 'MPC Custody Node', uptime: 99.72, lastBlock: null, status: 'ONLINE', icon: 'bi-safe-fill', color: '#f59e0b' },
                { node: 'Mercury / Column N.A.', operator: 'Mercury Technologies', type: 'Banking Rail', uptime: 99.91, lastBlock: null, status: 'ONLINE', icon: 'bi-bank2', color: '#a78bfa' },
              ].map((n, i) => (
                <div key={i} className="col-md-3 mb-3">
                  <div className="card border-0 shadow-sm h-100" style={{ background: '#0f172a', color: '#e2e8f0' }}>
                    <div className="card-body">
                      <div className="d-flex align-items-center gap-2 mb-3">
                        <i className={`bi ${n.icon} fs-4`} style={{ color: n.color }}></i>
                        <div>
                          <strong className="d-block" style={{ fontSize: '0.88rem' }}>{n.node}</strong>
                          <small style={{ color: '#94a3b8', fontSize: '0.72rem' }}>{n.operator}</small>
                        </div>
                      </div>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <small style={{ color: '#94a3b8' }}>Uptime</small>
                        <strong className="text-success">{n.uptime}%</strong>
                      </div>
                      <div className="progress mb-2" style={{ height: 4 }}>
                        <div className="progress-bar bg-success" style={{ width: `${n.uptime}%` }}></div>
                      </div>
                      <div className="d-flex justify-content-between align-items-center">
                        <small style={{ color: '#94a3b8' }}>{n.type}</small>
                        <span className="badge bg-success" style={{ fontSize: '0.65rem' }}>{n.status}</span>
                      </div>
                      {n.lastBlock && (
                        <div className="mt-2">
                          <small style={{ color: '#64748b', fontSize: '0.72rem' }}>Last Block: {n.lastBlock.toLocaleString()}</small>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Settlement Transaction Quality */}
            <div className="row mb-4">
              <div className="col-md-8">
                <div className="card border-0 shadow-sm">
                  <div className="card-header bg-dark text-white">
                    <h5 className="mb-0"><i className="bi bi-diagram-3-fill me-2"></i>Settlement Transaction Quality</h5>
                  </div>
                  <div className="card-body p-0">
                    <div className="table-responsive">
                      <table className="table table-hover mb-0">
                        <thead className="table-dark">
                          <tr>
                            <th>Metric</th>
                            <th>Current</th>
                            <th>Target</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {[
                            { metric: 'DvP Settlement Finality', current: 'T+0 (deterministic)', target: 'T+0', status: 'PASS' },
                            { metric: 'Atomic Rollback Rate', current: '0.00%', target: '< 1%', status: 'PASS' },
                            { metric: 'MPC Quorum Success Rate', current: '99.72%', target: '> 99%', status: 'PASS' },
                            { metric: 'Collateral Adequacy Pass Rate', current: '100%', target: '100%', status: 'PASS' },
                            { metric: 'Sanctions Screening Latency', current: '< 200ms', target: '< 500ms', status: 'PASS' },
                            { metric: 'ISO 20022 Message Chain Integrity', current: '6/6 messages', target: '6/6', status: 'PASS' },
                            { metric: 'Canton Node Consensus Latency', current: '< 2s', target: '< 5s', status: 'PASS' },
                            { metric: 'Document Key Generation', current: 'AES-256-GCM', target: 'AES-256', status: 'PASS' },
                            { metric: 'Cross-Rail ID Propagation', current: 'UETR + Canton ID + TX Hash', target: '3/3', status: 'PASS' },
                          ].map((m, i) => (
                            <tr key={i}>
                              <td><strong style={{ color: 'black' }}>{m.metric}</strong></td>
                              <td style={{ color: 'black' }}>{m.current}</td>
                              <td style={{ color: 'black' }}>{m.target}</td>
                              <td><span className="badge bg-success">{m.status}</span></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-md-4">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-success text-white">
                    <h5 className="mb-0"><i className="bi bi-patch-check-fill me-2"></i>Protocol Health</h5>
                  </div>
                  <div className="card-body text-center">
                    <div className="display-3 text-success fw-bold mb-2">A+</div>
                    <h6 style={{ color: 'black' }}>Protocol Quality Grade</h6>
                    <hr />
                    <div className="text-start">
                      {[
                        { label: 'Deterministic gates', val: '7/7 binary' },
                        { label: 'Failure resolution rate', val: '100%' },
                        { label: 'Avg node uptime', val: '99.86%' },
                        { label: 'Sandbox tests run', val: sandboxReceipt ? '1' : '0' },
                        { label: 'Mainnet deployment', val: 'Awaiting IS' },
                      ].map((item, i) => (
                        <div key={i} className="d-flex justify-content-between mb-2">
                          <small style={{ color: 'black' }}>{item.label}</small>
                          <small className="fw-bold" style={{ color: 'black' }}>{item.val}</small>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Uptime History */}
            <div className="card border-0 shadow-sm mb-4">
              <div className="card-header bg-primary text-white">
                <h5 className="mb-0"><i className="bi bi-clock-history me-2"></i>7-Day Node Uptime History</h5>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-sm table-hover mb-0">
                    <thead className="table-light">
                      <tr>
                        <th style={{ color: 'black' }}>Node</th>
                        {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map(d => <th key={d} className="text-center" style={{ color: 'black' }}>{d}</th>)}
                        <th className="text-center" style={{ color: 'black' }}>Avg</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { name: 'KRO-Mainnet-1', days: [99.9, 99.8, 99.9, 99.9, 100, 99.7, 99.9] },
                        { name: 'pio-mainnet-1', days: [100, 99.9, 100, 99.9, 99.9, 100, 99.9] },
                        { name: 'BitGo Enterprise', days: [99.8, 99.6, 99.9, 99.7, 99.8, 99.5, 99.7] },
                        { name: 'Mercury / Column', days: [100, 99.9, 99.8, 100, 99.9, 99.9, 99.9] },
                      ].map((row, i) => {
                        const avg = (row.days.reduce((a, b) => a + b, 0) / row.days.length).toFixed(2);
                        return (
                          <tr key={i}>
                            <td><strong style={{ color: 'black' }}>{row.name}</strong></td>
                            {row.days.map((d, j) => (
                              <td key={j} className="text-center">
                                <span className={`badge bg-${d >= 99.9 ? 'success' : d >= 99.5 ? 'info' : 'warning'}`} style={{ fontSize: '0.72rem' }}>{d}%</span>
                              </td>
                            ))}
                            <td className="text-center"><strong style={{ color: 'black' }}>{avg}%</strong></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Recent Test Results */}
      {testResults.size > 0 && (
        <div className="row mt-4">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-dark text-white">
                <h5 className="mb-0">Recent Test Results</h5>
              </div>
              <div className="card-body">
                <div className="row">
                  {Array.from(testResults.values()).slice(-4).map((result) => (
                    <div key={result.testId} className="col-md-3 mb-3">
                      <div className="card">
                        <div className="card-body">
                          <h6 className="card-title text-capitalize" style={{ color: 'black' }}>
                            {result.testType} Test
                          </h6>
                          <div className={`badge bg-${result.status === 'passed' ? 'success' : 'danger'} mb-2`}>
                            {result.status.toUpperCase()}
                          </div>
                          <div style={{ color: 'black' }}>Score: {result.score.toFixed(1)}</div>
                          <div style={{ color: 'black' }}>Issues: {result.issues}</div>
                          <small className="text-muted" style={{ color: 'black !important' }}>
                            {result.executionTime}s execution time
                          </small>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BreadQualityControl;